import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";

export default function CartPage() {
  const { cart, updateItem, removeItem, checkout, busy } = useCart();
  const lines = cart?.lines || [];
  const subtotal = cart?.cost?.subtotalAmount;

  return (
    <div>
      <PageHeader eyebrow="Your haul" title="Cart" />
      <div className="px-5 py-12 sm:px-10 lg:px-16">
        {lines.length === 0 ? (
          <Reveal>
            <div className="flex flex-col items-center gap-6 border border-border py-20 text-center">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Your cart is empty
              </p>
              <p className="font-display text-2xl font-bold uppercase tracking-tight">
                The trail is waiting.
              </p>
              <Link
                to="/shop"
                data-testid="cart-page-shop-link"
                className="border border-foreground px-8 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-foreground hover:text-background"
              >
                Shop now
              </Link>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
            <div>
              {lines.map((line) => {
                const m = line.merchandise;
                const img = m?.image?.url || m?.product?.featuredImage?.url;
                return (
                  <div
                    key={line.id}
                    data-testid={`cart-page-line-${line.id.slice(-6)}`}
                    className="flex gap-5 border-b border-border py-6"
                  >
                    <Link
                      to={`/product/${m?.product?.handle}`}
                      className="h-28 w-24 shrink-0 overflow-hidden bg-secondary"
                    >
                      {img && <img src={img} alt={m?.product?.title} className="h-full w-full object-cover" />}
                    </Link>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Link
                            to={`/product/${m?.product?.handle}`}
                            className="font-display text-base font-semibold uppercase tracking-wide hover:text-signal"
                          >
                            {m?.product?.title}
                          </Link>
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                            {m?.title}
                          </p>
                        </div>
                        <button
                          onClick={() => removeItem(line.id)}
                          data-testid={`cart-page-remove-${line.id.slice(-6)}`}
                          aria-label="Remove item"
                          className="p-1 text-muted-foreground transition-colors hover:text-signal"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-auto flex items-center justify-between">
                        <div className="flex items-center border border-border">
                          <button
                            onClick={() =>
                              line.quantity > 1
                                ? updateItem(line.id, line.quantity - 1)
                                : removeItem(line.id)
                            }
                            disabled={busy}
                            aria-label="Decrease quantity"
                            className="p-2.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-10 text-center font-mono text-xs">{line.quantity}</span>
                          <button
                            onClick={() => updateItem(line.id, line.quantity + 1)}
                            disabled={busy}
                            aria-label="Increase quantity"
                            className="p-2.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="font-mono text-sm">{money(line.cost?.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="h-fit border border-border p-6 lg:sticky lg:top-24" data-testid="cart-summary">
              <h2 className="font-display text-lg font-bold uppercase tracking-tight">
                Summary
              </h2>
              <div className="mt-5 space-y-3 font-mono text-xs uppercase tracking-[0.15em]">
                <div className="flex justify-between text-muted-foreground">
                  <span>Items</span>
                  <span>{cart?.totalQuantity}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping</span>
                  <span>At checkout</span>
                </div>
                <div className="flex justify-between border-t border-border pt-3 text-base">
                  <span>Subtotal</span>
                  <span data-testid="cart-page-subtotal">{money(subtotal)}</span>
                </div>
              </div>
              <button
                onClick={checkout}
                data-testid="cart-page-checkout-button"
                className="group mt-6 flex w-full items-center justify-center gap-2 bg-signal py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-signal/90"
              >
                Checkout
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                Secure checkout powered by Shopify
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
